import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';
import { API_ERROR_CODES } from '@hrms/shared';
import { fieldErrors, toApiError } from '@/lib/api-client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { useAuth } from './use-auth';
import { AuthLayout } from './auth-layout';
import { loginFormSchema, type LoginFormValues } from './auth-schemas';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { email: '', password: '' },
  });

  // Where the user was headed before the guard intercepted them.
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      const user = await login(values);
      void navigate(user.mustChangePassword ? '/change-password' : from, { replace: true });
    } catch (error) {
      const apiError = toApiError(error);

      // Field-level messages go on the fields; everything else is a banner.
      const fields = fieldErrors(error);
      for (const [name, messages] of Object.entries(fields)) {
        if (name === 'email' || name === 'password') {
          setError(name, { message: messages[0] });
        }
      }

      if (Object.keys(fields).length === 0) {
        setFormError(
          apiError.code === API_ERROR_CODES.ACCOUNT_LOCKED ||
            apiError.code === API_ERROR_CODES.ACCOUNT_INACTIVE
            ? apiError.message
            : // Never distinguish "no such account" from "wrong password" in
              // the UI — the API already refuses to, and echoing anything more
              // specific would undo that.
              'Incorrect email or password.',
        );
      }
    }
  });

  return (
    <AuthLayout
      title="Sign in"
      description="Enter your credentials to access the HR system."
      footer={
        <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground">
          Forgot your password?
        </Link>
      }
    >
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-4" noValidate>
        {formError && (
          <Alert variant="destructive">
            <AlertCircle aria-hidden="true" />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <FormField label="Email" error={errors.email?.message} required>
          {(props) => (
            <Input
              {...props}
              {...register('email')}
              type="email"
              autoComplete="username"
              autoFocus
              placeholder="you@kiobytes.ph"
            />
          )}
        </FormField>

        <FormField label="Password" error={errors.password?.message} required>
          {(props) => (
            <div className="relative">
              <Input
                {...props}
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="pr-9"
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          )}
        </FormField>

        <Button type="submit" className="w-full" loading={isSubmitting}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
